import { skipToken } from "@reduxjs/toolkit/query";
import { Eye, Pencil, Plus } from "lucide-react";
import { useState } from "react";
import type { SingleValue } from "react-select";

import { ContentContainer, PageLayout, PageTitle } from "components/layout";
import { DataTable, type ExtendedColumnDef } from "components/Datatable";
import { SelectInput } from "components/Select";
import { Button } from "components/ui/button";

import { useGetStoresOptionsQuery } from "@/services/store/select-options";
import { useGetStoreBankAccountsQuery } from "@/services/store-bank-account";
import type {
  Account,
  AccountStatusFilter,
  ListAccountsParams,
} from "@/services/dtos/store-bank-account.dto";

import { AccountFormModal } from "./components/account-form";
import { StoreViewModal } from "./components/store-view";
import {
  CLOSED_ACCOUNT_FORM_MODAL_STATE,
  CLOSED_STORE_VIEW_MODAL_STATE,
  DEFAULT_STATUS_FILTER,
  EDIT_BUTTON_LABEL,
  ROW_ACTION_BUTTON_CLASS,
  ROW_ACTION_ICON_CLASS,
  EMPTY_STATE_MESSAGES,
  INCLUDE_BUTTON_LABEL,
  PAGE_DESCRIPTION,
  PAGE_TITLE,
  STATUS_FILTER_OPTIONS,
  STATUS_LABEL_ID,
  STATUS_SELECT_LABEL,
  STORE_BANK_ACCOUNT_COLUMNS,
  STORE_LABEL_ID,
  STORE_SELECT_LABEL,
  STORE_SELECT_PLACEHOLDER,
  STORE_VIEW_BUTTON_LABEL,
  TABLE_COLUMN_LABELS,
  buildCreateAccountModalState,
  buildEditAccountModalState,
  buildOpenStoreViewModalState,
  resolveAccountFormStoreCode,
  type AccountFormModalState,
  type StatusFilterOption,
  type StoreSelectOption,
  type StoreViewModalState,
} from "./types";

export default function StoreBankAccount() {
  const [selectedStore, setSelectedStore] = useState<StoreSelectOption | null>(
    null,
  );
  const [statusFilter, setStatusFilter] = useState<AccountStatusFilter>(
    DEFAULT_STATUS_FILTER,
  );
  const [accountFormModalState, setAccountFormModalState] =
    useState<AccountFormModalState>(CLOSED_ACCOUNT_FORM_MODAL_STATE);
  const [storeViewModalState, setStoreViewModalState] =
    useState<StoreViewModalState>(CLOSED_STORE_VIEW_MODAL_STATE);

  const { data: storeOptions = [] } = useGetStoresOptionsQuery();

  const queryArg: ListAccountsParams | typeof skipToken = selectedStore
    ? { storeCode: Number(selectedStore.value), status: statusFilter }
    : skipToken;

  const { data: accounts, isFetching } =
    useGetStoreBankAccountsQuery(queryArg);

  const handleStoreChange = (option: SingleValue<StoreSelectOption>): void => {
    setSelectedStore(option ?? null);
  };

  const handleStatusChange = (
    option: SingleValue<StatusFilterOption>,
  ): void => {
    setStatusFilter(option?.value ?? DEFAULT_STATUS_FILTER);
  };

  const handleIncludeClick = (): void => {
    if (!selectedStore) return;
    setAccountFormModalState(buildCreateAccountModalState());
  };

  const handleViewStore = (): void => {
    if (!selectedStore) return;
    setStoreViewModalState(
      buildOpenStoreViewModalState(Number(selectedStore.value)),
    );
  };

  const handleEditAccount = (account: Account): void => {
    setAccountFormModalState(buildEditAccountModalState(account));
  };

  const handleCloseAccountFormModal = (): void => {
    setAccountFormModalState(CLOSED_ACCOUNT_FORM_MODAL_STATE);
  };

  const handleCloseStoreViewModal = (): void => {
    setStoreViewModalState(CLOSED_STORE_VIEW_MODAL_STATE);
  };

  const selectedStatusOption =
    STATUS_FILTER_OPTIONS.find((option) => option.value === statusFilter) ??
    STATUS_FILTER_OPTIONS[0];

  const columns: ExtendedColumnDef<Account>[] = [
    ...STORE_BANK_ACCOUNT_COLUMNS,
    {
      id: "actions",
      header: TABLE_COLUMN_LABELS.ACTIONS,
      cell: ({ row }) => (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className={ROW_ACTION_BUTTON_CLASS}
          onClick={() => handleEditAccount(row.original)}
        >
          <Pencil className={ROW_ACTION_ICON_CLASS} />
          {EDIT_BUTTON_LABEL}
        </Button>
      ),
    },
  ];

  const emptyMessage = selectedStore
    ? EMPTY_STATE_MESSAGES.NO_RESULTS
    : EMPTY_STATE_MESSAGES.NO_STORE_SELECTED;

  return (
    <PageLayout>
      <PageTitle title={PAGE_TITLE} description={PAGE_DESCRIPTION} />
      <ContentContainer>
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="flex flex-col gap-0.5 min-w-[220px]">
                <span
                  id={STORE_LABEL_ID}
                  className="text-xs font-medium text-muted-foreground"
                >
                  {STORE_SELECT_LABEL}
                </span>
                <SelectInput
                  options={storeOptions}
                  value={selectedStore}
                  onChange={handleStoreChange}
                  placeholder={STORE_SELECT_PLACEHOLDER}
                  aria-labelledby={STORE_LABEL_ID}
                  isClearable
                />
              </div>
              <div className="flex flex-col gap-0.5 min-w-[160px]">
                <span
                  id={STATUS_LABEL_ID}
                  className="text-xs font-medium text-muted-foreground"
                >
                  {STATUS_SELECT_LABEL}
                </span>
                <SelectInput
                  options={STATUS_FILTER_OPTIONS}
                  value={selectedStatusOption}
                  onChange={handleStatusChange}
                  aria-labelledby={STATUS_LABEL_ID}
                  isClearable={false}
                />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleViewStore}
                disabled={!selectedStore}
              >
                <Eye className="h-4 w-4 mr-2" />
                {STORE_VIEW_BUTTON_LABEL}
              </Button>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleIncludeClick}
              disabled={!selectedStore}
            >
              <Plus className="h-4 w-4 mr-2" />
              {INCLUDE_BUTTON_LABEL}
            </Button>
          </div>

          <DataTable
            columns={columns}
            data={accounts ?? []}
            isLoading={isFetching}
            emptyMessage={emptyMessage}
          />
        </div>
      </ContentContainer>

      <AccountFormModal
        open={accountFormModalState.open}
        mode={accountFormModalState.mode}
        storeCode={resolveAccountFormStoreCode(
          accountFormModalState,
          selectedStore ? Number(selectedStore.value) : null,
        )}
        account={accountFormModalState.account}
        onClose={handleCloseAccountFormModal}
      />

      <StoreViewModal
        open={storeViewModalState.open}
        storeCode={storeViewModalState.storeCode}
        onClose={handleCloseStoreViewModal}
      />
    </PageLayout>
  );
}
